import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Building2, Users, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { UpgradeRequest } from '../../types/upgrade';

type Props = {
  requests: UpgradeRequest[];
};

export const RequestStats = ({ requests }: Props) => {
  const { translations } = useLanguage();

  // Calculate statistics
  const stats = React.useMemo(() => {
    const hotelStats: Record<string, number> = {};
    const organizerStats: Record<string, number> = {};
    let completedCount = 0;
    let pendingCount = 0;
    let rejectedCount = 0;

    requests.forEach(request => {
      // Hotel stats
      hotelStats[request.fromHotel] = (hotelStats[request.fromHotel] || 0) + 1;

      // Organizer stats
      organizerStats[request.organizerName] = (organizerStats[request.organizerName] || 0) + 1;

      // Status stats
      if (request.status === 'completed') {
        completedCount++;
      } else if (['pending', 'approved', 'payment_pending'].includes(request.status)) {
        pendingCount++;
      } else if (request.status === 'rejected') {
        rejectedCount++;
      }
    });

    return {
      hotels: hotelStats,
      organizers: organizerStats,
      totalRequests: requests.length,
      completedRequests: completedCount,
      pendingRequests: pendingCount,
      rejectedRequests: rejectedCount
    };
  }, [requests]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {/* Overview Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="col-span-full grid grid-cols-2 md:grid-cols-4 gap-4"
      >
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <TrendingUp className="text-primary-500" size={24} />
            </div>
            <div>
              <div className="text-sm text-gray-600">Total Requests</div>
              <div className="text-2xl font-bold text-gray-900">{stats.totalRequests}</div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <CheckCircle className="text-green-500" size={24} />
            </div>
            <div>
              <div className="text-sm text-gray-600">Completed</div>
              <div className="text-2xl font-bold text-gray-900">{stats.completedRequests}</div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <Clock className="text-yellow-500" size={24} />
            </div>
            <div>
              <div className="text-sm text-gray-600">Pending</div>
              <div className="text-2xl font-bold text-gray-900">{stats.pendingRequests}</div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <XCircle className="text-red-500" size={24} />
            </div>
            <div>
              <div className="text-sm text-gray-600">Rejected</div>
              <div className="text-2xl font-bold text-gray-900">{stats.rejectedRequests}</div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Hotel Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white p-6 rounded-xl shadow-sm"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Building2 className="text-blue-500" size={24} />
          </div>
          <h3 className="text-lg font-semibold">Hotels Overview</h3>
        </div>
        <div className="space-y-4">
          {Object.entries(stats.hotels)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5)
            .map(([hotel, count]) => (
              <div key={hotel} className="flex items-center justify-between">
                <span className="text-gray-600 text-sm truncate flex-1">{hotel}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-blue-500 h-1.5 rounded-full" 
                      style={{ width: `${Math.min((count / stats.totalRequests) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <span className="font-medium">{count}</span>
                  <span className="text-xs text-gray-500">
                    ({Math.round((count / stats.totalRequests) * 100)}%)
                  </span>
                </div>
              </div>
            ))}
        </div>
      </motion.div>

      {/* Organizer Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white p-6 rounded-xl shadow-sm"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-50 rounded-lg">
            <Users className="text-purple-500" size={24} />
          </div>
          <h3 className="text-lg font-semibold">Organizers Overview</h3>
        </div>
        <div className="space-y-4">
          {Object.entries(stats.organizers)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5)
            .map(([organizer, count]) => (
              <div key={organizer} className="flex items-center justify-between">
                <span className="text-gray-600 text-sm truncate flex-1">{organizer}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-purple-500 h-1.5 rounded-full" 
                      style={{ width: `${Math.min((count / stats.totalRequests) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <span className="font-medium">{count}</span>
                  <span className="text-xs text-gray-500">
                    ({Math.round((count / stats.totalRequests) * 100)}%)
                  </span>
                </div>
              </div>
            ))}
        </div>
      </motion.div>

      {/* Status Distribution */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white p-6 rounded-xl shadow-sm"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-yellow-50 rounded-lg">
            <AlertTriangle className="text-yellow-500" size={24} />
          </div>
          <h3 className="text-lg font-semibold">Status Overview</h3>
        </div>
        <div className="space-y-4">
          <div className="relative pt-1">
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="text-xs font-semibold inline-block text-green-600">
                  Completed
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-green-600">
                  {stats.totalRequests > 0 ? Math.round((stats.completedRequests / stats.totalRequests) * 100) : 0}%
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-green-200">
              <div style={{ width: `${stats.totalRequests > 0 ? (stats.completedRequests / stats.totalRequests) * 100 : 0}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500"></div>
            </div>
            
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="text-xs font-semibold inline-block text-yellow-600">
                  Pending
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-yellow-600">
                  {stats.totalRequests > 0 ? Math.round((stats.pendingRequests / stats.totalRequests) * 100) : 0}%
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-yellow-200">
              <div style={{ width: `${stats.totalRequests > 0 ? (stats.pendingRequests / stats.totalRequests) * 100 : 0}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-yellow-500"></div>
            </div>
            
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="text-xs font-semibold inline-block text-red-600">
                  Rejected
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-red-600">
                  {stats.totalRequests > 0 ? Math.round((stats.rejectedRequests / stats.totalRequests) * 100) : 0}%
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-red-200">
              <div style={{ width: `${stats.totalRequests > 0 ? (stats.rejectedRequests / stats.totalRequests) * 100 : 0}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-red-500"></div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};